import { useEffect, useRef, useState } from "react";
import { Phone, Menu, X, ArrowRight } from "lucide-react";
import InstallPWA from "@/components/InstallPWA";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragX, setDragX] = useState(0); // live drag offset for close gesture
  const dragRef = useRef({ startX: 0, startT: 0, active: false, lastDx: 0 });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [menuOpen]);

  // Edge-swipe to OPEN drawer from RIGHT edge (mobile only)
  useEffect(() => {
    const EDGE = 20;
    const THRESHOLD = 60;
    const state = { startX: 0, startT: 0, watching: false };
    const onStart = (e) => {
      if (window.innerWidth >= 1024) return;
      if (menuOpen) return;
      const t = e.touches[0];
      if (t.clientX >= window.innerWidth - EDGE) {
        state.startX = t.clientX;
        state.startT = Date.now();
        state.watching = true;
      } else {
        state.watching = false;
      }
    };
    const onEnd = (e) => {
      if (!state.watching) return;
      state.watching = false;
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      const dx = t.clientX - state.startX; // negative = swiped left
      const dt = Date.now() - state.startT;
      const v = dt > 0 ? Math.abs(dx) / dt : 0;
      if (dx < -THRESHOLD || (dx < -20 && v > 0.5)) {
        setMenuOpen(true);
      }
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [menuOpen]);

  // Swipe-to-close handlers (on drawer): drag right > 80px or flick
  const onTouchStart = (e) => {
    if (!menuOpen) return;
    const t = e.touches[0];
    dragRef.current = {
      startX: t.clientX,
      startT: Date.now(),
      active: true,
      lastDx: 0,
    };
  };
  const onTouchMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.touches[0].clientX - dragRef.current.startX;
    const clamped = Math.max(0, dx); // only allow drag to the right
    dragRef.current.lastDx = clamped;
    setDragX(clamped);
  };
  const onTouchEnd = () => {
    if (!dragRef.current.active) return;
    const dx = dragRef.current.lastDx;
    const dt = Date.now() - dragRef.current.startT;
    const v = dt > 0 ? Math.abs(dx) / dt : 0;
    dragRef.current.active = false;
    if (dx > 80 || (dx > 20 && v > 0.5)) {
      setMenuOpen(false);
    }
    setDragX(0);
  };

  const links = [
    { href: "#sluzby", label: "Služby" },
    { href: "/dom-na-kluc", label: "Dom na kľúč" },
    { href: "#portfolio", label: "Portfólio" },
    { href: "#cennik", label: "Cenník" },
    { href: "#obhliadka", label: "Obhliadka" },
    { href: "/blog", label: "Blog" },
    { href: "/projekty", label: "Mapa projektov" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass py-4" : "bg-transparent py-6"
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <a href="#" className="relative z-10" data-testid="logo">
              <span className="text-2xl font-black tracking-tight">
                Super<span className="text-[#00E5FF] neon-cyan">Majstri</span>
              </span>
            </a>

            <nav className="hidden lg:flex items-center gap-8">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm font-medium"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:+421907649964"
                className="flex items-center gap-2 text-zinc-400 hover:text-[#00E5FF] transition-colors"
              >
                <Phone size={16} />
                <span className="mono text-sm">0907 64 99 64</span>
              </a>
              <a href="#kontakt" className="btn-primary text-sm" data-testid="header-cta">
                Nezáväzný plán
              </a>
            </nav>

            <button
              onClick={() => setMenuOpen(true)}
              className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Otvoriť menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      {menuOpen && (
        <button
          aria-label="Zavrieť menu"
          onClick={() => setMenuOpen(false)}
          className="lg:hidden fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile slide-in drawer (from right) */}
      <aside
        className={`
          lg:hidden fixed top-0 right-0 z-[61] h-full w-80 max-w-[85vw]
          bg-[#111] border-l border-white/10
          flex flex-col
          ${menuOpen ? "translate-x-0" : "translate-x-full"}
        `}
        style={{
          transform: dragX ? `translateX(${dragX}px)` : undefined,
          transition: dragX ? "none" : "transform 300ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <span className="text-xl font-black tracking-tight">
            Super<span className="text-[#00E5FF]">Majstri</span>
          </span>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Zavrieť menu"
            data-testid="mobile-menu-close"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-5 space-y-1">
          {links.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-4 py-4 rounded-xl text-white text-base font-semibold hover:bg-white/5 hover:text-[#00E5FF] transition-colors group"
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                animationDelay: `${i * 30}ms`,
              }}
            >
              <span>{link.label}</span>
              <ArrowRight
                size={16}
                className="text-zinc-600 group-hover:text-[#00E5FF] group-hover:translate-x-1 transition-all"
              />
            </a>
          ))}
        </nav>

        <div className="p-5 border-t border-white/10 space-y-3">
          <InstallPWA variant="drawer" />
          <a
            href="tel:+421907649964"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-[#00E5FF] font-bold hover:bg-white/10 transition-colors"
            data-testid="mobile-phone-cta"
          >
            <Phone size={16} />
            <span className="mono">0907 64 99 64</span>
          </a>
          <a
            href="#kontakt"
            onClick={() => setMenuOpen(false)}
            className="btn-primary w-full text-center"
            data-testid="mobile-quote-cta"
          >
            Nezáväzný plán
          </a>
        </div>
      </aside>
    </>
  );
}
